import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { SERVICES, TRUST } from "@/data/site";

const HERO = "https://images.unsplash.com/photo-1740209475472-aa7d280f7452?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxjbmMlMjBtYWNoaW5pbmd8ZW58MHx8fHwxNzgyNjc5MjM5fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  useSeo(
    "Edgeworks Manufacturing | CNC Machining in Eastern Ontario",
    "Owner-operated CNC machining in Eastern Ontario. Precision CNC milling and turning for prototypes, replacement parts, and small production runs. Request a quote from a drawing."
  );

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-neutral-800">
        <div className="absolute inset-0">
          <img src={HERO} alt="CNC machine cutting a metal part" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-neutral-950/80" />
          <div className="absolute inset-0 bp-grid opacity-40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8 lg:py-40">
          <div className="max-w-3xl fade-up">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-orange-500">
              Eastern Ontario · Cornwall Area Machine Shop
            </p>
            <h1 className="mt-5 font-heading text-4xl font-black leading-none tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Precision CNC Machining for Prototypes, Repairs, and Small Production Runs
            </h1>
            <p className="mt-6 max-w-2xl text-base text-neutral-300 sm:text-lg">
              Edgeworks Manufacturing helps local manufacturers, repair shops, and equipment builders
              get accurate machined parts with clear communication and practical lead times.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-a-quote"
                data-testid="hero-quote-btn"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 px-7 py-3.5 font-semibold text-white transition-colors hover:bg-orange-600"
              >
                Request a Quote <ArrowRight size={18} />
              </Link>
              <Link
                to="/capabilities"
                data-testid="hero-capabilities-btn"
                className="inline-flex items-center justify-center gap-2 border border-neutral-600 px-7 py-3.5 font-semibold text-white transition-colors hover:border-neutral-400 hover:bg-neutral-900"
              >
                View Capabilities
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-testid="services-section">
        <div className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-orange-500">What I do</p>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Machining services
          </h2>
          <p className="mt-3 text-neutral-400">
            A practical range of CNC work for one-off parts through to repeatable small batches.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-px border border-neutral-800 bg-neutral-800 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <div key={s.title} className="bg-neutral-950 p-7 transition-colors hover:bg-neutral-900" data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <s.icon className="text-orange-500" size={26} strokeWidth={1.5} />
              <h3 className="mt-4 font-heading text-lg font-bold text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className="border-t border-neutral-800 bg-neutral-900" data-testid="trust-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-orange-500">Why work with Edgeworks</p>
              <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Built on straightforward, dependable work
              </h2>
              <p className="mt-4 text-neutral-400">
                A small shop focused on clear communication, attention to detail, and practical
                solutions for real manufacturing problems.
              </p>
            </div>
            <div className="grid gap-px border border-neutral-800 bg-neutral-800 sm:grid-cols-2">
              {TRUST.map((t) => (
                <div key={t.title} className="bg-neutral-950 p-6">
                  <div className="flex items-start gap-3">
                    <Check className="mt-0.5 shrink-0 text-orange-500" size={20} />
                    <div>
                      <h3 className="font-heading font-bold text-white">{t.title}</h3>
                      <p className="mt-1 text-sm text-neutral-400">{t.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
